import ComingSoonStats from '@/components/game-result/ComingSoonStats';
import { FairwayAccuracy } from '@/components/game-result/FairwayAccuracy';
import ScoreByPar from '@/components/game-result/ScoreByPar';
import ScoreDistribution from '@/components/game-result/ScoreDistribution';
import { useUserProfile } from '@/context/UserProfileContext';
import { AggregatedStats } from '@/types';
import { api } from '@/utils/api';
import {
  IonContent,
  IonHeader,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Default initial stats state
const defaultStats: AggregatedStats = {
  totalGames: 0,
  avgScore: 0,
  handicap: 0,
  eagles: 0,
  birdies: 0,
  pars: 0,
  bogeys: 0,
  doubleBogeys: 0,
  totalPutts: 0,
  holesWithPuttsData: 0,
  greensInRegulationCount: 0,
  girOpportunities: 0,
  fairwayAccuracy: {
    left: 0,
    middle: 0,
    right: 0,
    total: 0
  },
  par3Score: {
    total: 0,
    toPar: 0,
    count: 0
  },
  par4Score: {
    total: 0,
    toPar: 0,
    count: 0
  },
  par5Score: {
    total: 0,
    toPar: 0,
    count: 0
  }
}

const StatsPage: React.FC = () => {
  const { t } = useTranslation();
  const { profile } = useUserProfile();
  const [selectedSegment, setSelectedSegment] = useState<'all' | 'recent'>('all');
  const [stats, setStats] = useState<AggregatedStats>({ ...defaultStats, handicap: profile?.handicap || 0 });

  document.title = t('stats');

  useEffect(() => {
    if (!profile) return;

    const fetchStats = async () => {
      try {
        // Use dedicated stats endpoint with timeframe parameter
        const timeframeParam = selectedSegment === 'recent' ? '?timeframe=recent' : '';
        const response = await api.get(`/stats/${profile.userId}${timeframeParam}`);

        if (response.data) {
          // Server returns pre-aggregated stats
          setStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, [profile, selectedSegment]);

  const handleSegmentChange = (e: CustomEvent) => {
    const newSegment = e.detail.value as 'all' | 'recent';
    setSelectedSegment(newSegment);
  };

  return (
    <IonPage>
      <IonHeader translucent={true} className="md:hidden">
        <IonToolbar>
          <IonTitle>{t('insights')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color="dark" fullscreen={true}>
        <IonHeader collapse="condense">
          <IonToolbar color="dark" className='max-w-3xl mx-auto'>
            <IonTitle size="large" className='py-0'>{t('insights')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <div className="space-y-2 ion-padding max-w-3xl mx-auto">
          {/* Player handicap card */}
          <div>
            <div className="stats bg-card w-full">
              <div className="stat">
                <div className="stat-title text-foreground">{t('golf_handicap')}</div>
                <div className="stat-value text-foreground text-2xl">{profile?.handicap ? profile.handicap : '-'}</div>
              </div>

              <div className="stat">
                <div className="stat-title text-foreground">{t('rounds')}</div>
                <div className="stat-value text-foreground text-2xl">{stats?.totalGames}</div>
              </div>

              <div className="stat">
                <div className="stat-title text-foreground">{t('average_score')}</div>
                <div className="stat-value text-foreground text-2xl">{stats?.avgScore ? stats.avgScore : '-'}</div>
              </div>
            </div>
          </div>

          {/* Segment for All Games vs Recent Games */}
          <IonSegment
            value={selectedSegment}
            onIonChange={handleSegmentChange}
          >
            <IonSegmentButton value="all">
              <IonLabel>{t('all_games')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="recent">
              <IonLabel>{t('last_20')}</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {/* Statistics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ScoreDistribution stats={stats} />
            <ScoreByPar stats={stats} />
            <FairwayAccuracy stats={stats} />
            <div className="w-full h-[288p] bg-card rounded-2xl">
              <ComingSoonStats className="size-full" />
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default StatsPage;
